import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, Outlet } from '@tanstack/react-router';
import { ChevronDown, CircleUserRound, LogOut, Menu, Siren, X } from 'lucide-react';
import { useEffect, useId, useState } from 'react';

import { formatCountdown } from '@dob/i18n';

import { api } from '../api/client.ts';
import type { EmergencyCurrent, Inbox, Profile } from '../api/types.ts';
import {
  LocationProvider,
  useCurrentLocation,
  usePermissions,
  useSession,
} from '../auth/session.tsx';
import { Mark } from '../components/brand.tsx';
import { Button, ErrorAlert, Spinner } from '../components/ui.tsx';
import { LanguageToggle, ThemeToggle } from '../components/toggles.tsx';
import { useLang } from '../i18n.ts';
import { ForcedPasswordChange } from '../pages/auth/ForcedPasswordChange.tsx';
import { ConnectionBadge } from './ConnectionBadge.tsx';
import { visibleNav } from './nav.ts';

/** Everything behind sign-in. */
export function AppLayout() {
  const { status } = useSession();
  const profile = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<Profile>('/auth/me'),
    enabled: status === 'signedIn',
    staleTime: 60_000,
  });
  if (status === 'loading')
    return (
      <FullPage>
        <Spinner />
      </FullPage>
    );
  if (status === 'signedOut') return <Navigate to="/login" />;
  if (profile.isError) {
    return (
      <FullPage>
        <div className="flex w-full max-w-md flex-col gap-3">
          <ErrorAlert error={profile.error} />
          <RetryButton onRetry={() => void profile.refetch()} />
        </div>
      </FullPage>
    );
  }
  if (!profile.data)
    return (
      <FullPage>
        <Spinner />
      </FullPage>
    );
  if (profile.data.user.mustChangePassword) return <ForcedPasswordChange />;
  return (
    <LocationProvider>
      <Shell />
    </LocationProvider>
  );
}

function RetryButton({ onRetry }: { readonly onRetry: () => void }) {
  const { t } = useLang();
  return <Button onClick={onRetry}>{t('common.retry')}</Button>;
}

function FullPage({ children }: { readonly children: React.ReactNode }) {
  return <div className="flex min-h-dvh items-center justify-center p-4">{children}</div>;
}

function Shell() {
  const { t } = useLang();
  const [drawerOpen, setDrawerOpen] = useState(false);
  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[17rem_minmax(0,1fr)]">
      <a
        href="#main"
        className="sr-only z-50 rounded-lg bg-surface px-4 py-2 focus:not-sr-only focus:fixed focus:start-4 focus:top-4"
      >
        {t('app.skipToContent')}
      </a>
      <Sidebar
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
        }}
      />
      <div className="flex min-h-dvh min-w-0 flex-col">
        <EmergencyBanner />
        <TopBar
          onMenu={() => {
            setDrawerOpen(true);
          }}
        />
        <main id="main" tabIndex={-1} className="mx-auto w-full max-w-6xl flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// ── Sidebar ─────────────────────────────────────────────────────────────────

function Sidebar({ open, onClose }: { readonly open: boolean; readonly onClose: () => void }) {
  const { t } = useLang();
  const { can } = usePermissions();
  const inbox = useQuery({
    queryKey: ['inbox', 'unread'],
    queryFn: () => api.get<Inbox>('/inbox', { unread: 'true' }),
    refetchInterval: 60_000,
  });
  const unread = inbox.data?.unreadCount ?? 0;

  const nav = (
    <nav aria-label={t('nav.menu')} className="flex flex-col gap-6 px-3 py-4">
      {visibleNav(can).map((group) => (
        <div key={group.label}>
          <h2 className="mb-1.5 px-3 text-xs font-bold uppercase tracking-wide text-muted">
            {t(`nav.${group.label}`)}
          </h2>
          <ul className="flex flex-col gap-0.5">
            {group.items.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={onClose}
                    activeOptions={{ exact: item.to === '/' }}
                    className="relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-text hover:bg-surface-2"
                    activeProps={{
                      className:
                        'bg-surface-2 font-bold before:absolute before:inset-y-1.5 before:start-0 before:w-1 before:rounded-full before:bg-accent',
                      'aria-current': 'page',
                    }}
                  >
                    <Icon className="size-[1.125rem] shrink-0 text-muted" aria-hidden />
                    <span className="flex-1">{t(`nav.${item.label}`)}</span>
                    {item.to === '/inbox' && unread > 0 ? (
                      <span className="num rounded-full bg-primary px-2 py-0.5 text-xs font-bold text-on-primary">
                        {unread}
                      </span>
                    ) : null}
                    {!item.ready ? (
                      <span className="rounded-full border border-border px-1.5 text-[0.6875rem] text-muted">
                        {t('common.comingSoon')}
                      </span>
                    ) : null}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );

  const brand = (
    <div className="flex items-center gap-3 border-b border-border px-5 py-4">
      <Mark className="h-8 w-auto" />
      <div className="leading-tight">
        <p className="font-bold">{t('app.name')}</p>
        <p className="text-xs text-muted">{t('app.product')}</p>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop: always visible on the start side (right in Arabic, left in English). */}
      <aside className="sticky top-0 hidden h-dvh flex-col overflow-y-auto border-e border-border bg-surface lg:flex">
        {brand}
        {nav}
      </aside>
      {/* Phone and tablet: a drawer. */}
      {open ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            aria-label={t('common.close')}
            className="absolute inset-0 bg-black/50"
            onClick={onClose}
          />
          <aside className="absolute inset-y-0 start-0 flex w-72 flex-col overflow-y-auto bg-surface shadow-xl">
            <div className="flex items-center justify-between">
              {brand}
              <button
                type="button"
                onClick={onClose}
                className="me-3 rounded p-2 hover:bg-surface-2"
                aria-label={t('common.close')}
              >
                <X className="size-5" aria-hidden />
              </button>
            </div>
            {nav}
          </aside>
        </div>
      ) : null}
    </>
  );
}

// ── Top bar ─────────────────────────────────────────────────────────────────

function TopBar({ onMenu }: { readonly onMenu: () => void }) {
  const { t } = useLang();
  return (
    <header className="sticky top-0 z-30 flex flex-wrap items-center gap-2 border-b border-border bg-surface/95 px-3 py-2 backdrop-blur sm:px-6">
      <button
        type="button"
        onClick={onMenu}
        className="rounded-lg p-2 hover:bg-surface-2 lg:hidden"
        aria-label={t('nav.menu')}
      >
        <Menu className="size-5" aria-hidden />
      </button>
      <LocationSwitcher />
      <div className="flex-1" />
      <ConnectionBadge className="hidden sm:inline-flex" />
      <LanguageToggle />
      <ThemeToggle />
      <UserMenu />
    </header>
  );
}

function LocationSwitcher() {
  const { t, pick } = useLang();
  const { profile } = useSession();
  const { locationId, companyWide, setLocationId } = useCurrentLocation();
  const id = useId();
  if (!profile) return null;
  const single = !companyWide && profile.locations.length <= 1;
  return (
    <div className="flex items-center gap-2">
      <label htmlFor={id} className="sr-only">
        {t('location.switcher')}
      </label>
      <select
        id={id}
        title={t('location.switcherHint')}
        value={locationId ?? ''}
        disabled={single}
        onChange={(event) => {
          setLocationId(event.target.value === '' ? null : event.target.value);
        }}
        className="h-10 max-w-[16rem] rounded-lg border border-border-input bg-surface px-3 text-sm font-semibold disabled:opacity-100"
      >
        {companyWide ? <option value="">{t('location.allLocations')}</option> : null}
        {profile.locations.map((location) => (
          <option key={location.id} value={location.id}>
            {pick(location.nameAr, location.nameEn)}
            {location.isActive ? '' : ` — ${t('location.inactive')}`}
          </option>
        ))}
      </select>
    </div>
  );
}

function UserMenu() {
  const { t, pick } = useLang();
  const { profile, signOut } = useSession();
  const [open, setOpen] = useState(false);
  if (!profile) return null;
  const name = pick(profile.user.fullNameAr, profile.user.fullNameEn);
  return (
    <div className="relative">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
        }}
        className="inline-flex h-10 items-center gap-2 rounded-lg px-2 text-sm font-semibold hover:bg-surface-2"
      >
        <CircleUserRound className="size-5 text-muted" aria-hidden />
        <span className="hidden max-w-40 truncate md:inline">{name}</span>
        <ChevronDown className="size-4 text-muted" aria-hidden />
      </button>
      {open ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 cursor-default"
            aria-label={t('common.close')}
            onClick={() => {
              setOpen(false);
            }}
          />
          <div className="absolute end-0 z-50 mt-1 w-56 rounded-xl border border-border bg-surface p-1 shadow-lg">
            <p className="truncate px-3 py-2 text-sm font-bold md:hidden">{name}</p>
            <Link
              to="/account"
              onClick={() => {
                setOpen(false);
              }}
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-surface-2"
            >
              <CircleUserRound className="size-4" aria-hidden />
              {t('nav.account')}
            </Link>
            <button
              type="button"
              onClick={() => void signOut()}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-surface-2"
            >
              <LogOut className="size-4 rtl:-scale-x-100" aria-hidden />
              {t('auth.signOut')}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

// ── Emergency Mode banner (plan §7.6: red border, icon, countdown, always visible) ──

function EmergencyBanner() {
  const { t } = useLang();
  const current = useQuery({
    queryKey: ['emergency', 'current'],
    queryFn: () => api.get<EmergencyCurrent>('/emergency/current'),
    refetchInterval: 30_000,
  });
  const session = current.data?.session;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!session?.active) return;
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1_000);
    return () => {
      clearInterval(timer);
    };
  }, [session?.active]);
  if (!session?.active) return null;
  const left = Date.parse(session.expiresAt) - now;
  return (
    <div
      role="alert"
      className="flex items-center gap-3 border-y-4 border-dashed border-brand-yellow bg-emergency px-4 py-2 text-sm font-bold text-on-emergency"
    >
      <Siren className="size-5 shrink-0 animate-pulse" aria-hidden />
      <span>{t('emergency.banner', { time: formatCountdown(left) })}</span>
    </div>
  );
}
